import { type RefObject, useMemo, useRef } from 'react';

import { AddIcon, Button, Divider, ExcelIcon, type TypeSelectOption } from '@thefarmersfront/kitchen';

import type { IRowNode } from 'ag-grid-community';
import type { AgGridReact } from 'ag-grid-react';

import { Table, TableBottomField, TableTopField } from '~/components/common';

import { getDeliveryAgencyTrackingsColumnDefs } from '~/utils/delivery-agency/table';

import { DELIVERY_AGENCY_PAGE_SIZE } from '~/constants/delivery-agency';

import type { TypePagination } from '~/types/common.types';
import type { TypeDeliveryAgencyShippingLabel } from '~/types/delivery-agency.types';

import styles from './DeliveryAgencyTrackingTable.module.scss';

const DeliveryAgencyTrackingTable = ({
  data,
  pagination,
  selectedItems,
  isDownloadReady,
  gridRef,
  onChangePageLimit,
  onChangePage,
  onChangeRowSelection,
  onClickViewDetail,
  onClickPrintShippingLabel,
  onClickTrackingExcelDownload,
  onClickCancelDelivery,
  onClickCreateShippingLabel,
}: {
  data: TypeDeliveryAgencyShippingLabel[] | null;
  pagination: TypePagination;
  selectedItems: TypeDeliveryAgencyShippingLabel[];
  isDownloadReady: boolean;
  gridRef?: RefObject<AgGridReact>;
  onChangePageLimit: (value: TypeSelectOption<number>) => void;
  onChangePage: (value: number) => void;
  onChangeRowSelection: (selectedRows: IRowNode<TypeDeliveryAgencyShippingLabel>[]) => void;
  onClickViewDetail: (order: TypeDeliveryAgencyShippingLabel) => void;
  onClickPrintShippingLabel: () => void;
  onClickTrackingExcelDownload: () => void;
  onClickCancelDelivery: () => void;
  onClickCreateShippingLabel: () => void;
}) => {
  const onClickViewDetailRef = useRef(onClickViewDetail);
  onClickViewDetailRef.current = onClickViewDetail;

  const { columnDefs } = useMemo(
    () =>
      getDeliveryAgencyTrackingsColumnDefs({
        currentPage: pagination.currentPage - 1,
        pageSize: pagination.pageSize,
        onClickViewDetail: (order) => onClickViewDetailRef.current(order),
      }),
    [pagination.currentPage, pagination.pageSize]
  );

  return (
    <div className={styles['table-container']}>
      <TableTopField
        dataTotalCount={pagination.totalCount}
        rightButton={
          <div className={styles['table-top-right-field']}>
            <Button
              size="tiny"
              variant="outlined"
              color="tertiary"
              disabled={selectedItems.length === 0}
              onClick={onClickPrintShippingLabel}
            >
              운송장 출력
            </Button>
            <Divider className={styles['divider']} />
            <Button
              size="tiny"
              variant="outlined"
              color="tertiary"
              disabled={!isDownloadReady}
              leftIcon={<ExcelIcon size={16} />}
              onClick={onClickTrackingExcelDownload}
            >
              운송장 엑셀 전체 다운로드
            </Button>
            <Divider className={styles['divider']} />
            <Button
              size="tiny"
              variant="outlined"
              status="error"
              disabled={selectedItems.length === 0}
              onClick={onClickCancelDelivery}
            >
              배송 취소
            </Button>
            <Divider className={styles['divider']} />
            <Button size="tiny" color="secondary" leftIcon={<AddIcon size={16} />} onClick={onClickCreateShippingLabel}>
              추가 운송장 생성
            </Button>
          </div>
        }
      />
      <div className={styles['table-wrapper']}>
        <Table
          data={data}
          options={{ columnDefs }}
          gridRef={gridRef}
          rowStatusType={data === null ? 'INIT' : 'EMPTY'}
          onSelectionChange={onChangeRowSelection}
        />
      </div>
      <TableBottomField
        pageSizeOptions={Object.values(DELIVERY_AGENCY_PAGE_SIZE)}
        currentPage={pagination.currentPage - 1}
        totalCount={pagination.totalCount}
        pageSize={pagination.pageSize}
        onChangePageLimit={onChangePageLimit}
        onClickPage={(page) => onChangePage(page + 1)}
      />
    </div>
  );
};

export default DeliveryAgencyTrackingTable;
